/**
 * Maps human-readable keys to translation IDs for the shared "other
 * calculators" section rendered on every `/tools/*` page.
 *
 * One namespace rather than per-page keys, because each tool page renders the
 * OTHER tools' cards: a page cannot own the copy for a sibling it does not
 * know about. A new tool adds its own title + description here, and every
 * existing tool page can then render its card.
 *
 * The tool pages are gated to CH + DE, so `de-DE` is held to full parity on
 * this namespace. `de-AT`, `en-NL` and `nl` still exempt it in
 * `SKIP_NAMESPACES_PER_LOCALE` (scripts/lint-mirror-locales.ts) — translate +
 * drop an exemption when the tools open to that market.
 */
export const healthToolsKeys = {
  // Section chrome
  heading: "healthTools_heading",
  intro: "healthTools_intro",

  // One title + description per tool, contributed by that tool's own branch
  bmiTitle: "healthTools_bmiTitle",
  bmiDesc: "healthTools_bmiDesc",
  bmrTitle: "healthTools_bmrTitle",
  bmrDesc: "healthTools_bmrDesc",
  // The SCORE2 calculator is gated to CH alone, so unlike the four keys above
  // these two are exempt from de-DE parity as well (`SKIP_KEYS_PER_LOCALE` in
  // scripts/lint-mirror-locales.ts). Germany's /tools index never lists the
  // card, and next-intl inlines a whole catalog into every page's RSC payload,
  // so shipping the copy there would put a Swiss-only tool's description in
  // the HTML of every German page.
  score2Title: "healthTools_score2Title",
  score2Desc: "healthTools_score2Desc",
  // Same story as the SCORE2 pair above, for the same reason the copy is not
  // in de-DE.json: the FIB-4 calculator is gated to CH alone, so shipping its
  // card description to Germany would put a Swiss-only tool's copy in the HTML
  // of every German page. The reason it is CH-only differs (a missing
  // translation, not a missing calibration), but the exemption is identical
  // while it holds.
  fib4Title: "healthTools_fib4Title",
  fib4Desc: "healthTools_fib4Desc",
  // And again for sMAF-5, gated to CH alone for the same reason as FIB-4: a
  // missing translation rather than a missing calibration. Same exemption
  // while it holds.
  smaf5Title: "healthTools_smaf5Title",
  smaf5Desc: "healthTools_smaf5Desc",
  // And once more for the MAFLD criteria page, CH-only on the same grounds:
  // the definition carries no country term either, so the exemption is a
  // missing translation and nothing more.
  mafldTitle: "healthTools_mafldTitle",
  mafldDesc: "healthTools_mafldDesc",
} as const
